import React from 'react';
import { motion, MotionConfig, Variants } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.04 } },
};

const inView = {
  initial: 'hidden' as const,
  whileInView: 'show' as const,
  viewport: { once: true, margin: '-80px' },
};

const ArenaPreview: React.FC = () => {
  const navigate = useNavigate();

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate min-h-screen w-full bg-white">
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <div className="relative z-10">
          {/* ── Hero ── */}
          <section className="mx-auto max-w-4xl px-6 pt-20 pb-16 text-center sm:pt-28">
            <motion.div initial="hidden" animate="show" variants={stagger}>
              <motion.span variants={fadeUp} className="eyebrow">
                The Arena
              </motion.span>
              <motion.h1
                variants={fadeUp}
                className="mt-5 text-balance font-serif text-5xl leading-[1.05] tracking-tight text-charcoal sm:text-6xl lg:text-7xl"
              >
                Shape the discourse.
              </motion.h1>
              <motion.p
                variants={fadeUp}
                className="mx-auto mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-ink-muted sm:text-xl"
              >
                Create private debates on topics that matter. Watch AI agents steelman
                both sides in real time. Then share with the community for voting.
              </motion.p>
              <motion.div variants={fadeUp} className="mt-9">
                <button
                  onClick={() => navigate('/signup')}
                  className="btn-primary !px-8 !py-3.5 !text-base"
                >
                  Create your first debate
                </button>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-eyebrow text-ink-muted/70">
                  Free to start · Watch AI debate in real time
                </p>
              </motion.div>
            </motion.div>
          </section>

          {/* ── Sample debate in action ── */}
          <section className="mx-auto max-w-4xl px-6 pb-20">
            <motion.div {...inView} variants={fadeUp} className="card-frame overflow-hidden">
              {/* Topic header */}
              <div className="flex items-center justify-between gap-4 border-b border-black/[0.06] px-5 py-4 sm:px-6">
                <span className="truncate font-serif text-base text-charcoal sm:text-lg">
                  Should AI development be regulated by international treaties?
                </span>
                <span className="flex flex-shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-steel-blue" />
                  Live · Rebuttals
                </span>
              </div>

              {/* Two sides */}
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="border-b border-black/[0.06] p-5 sm:border-b-0 sm:border-r sm:p-6">
                  <div className="mb-2.5 flex items-center gap-2">
                    <span className="h-3 w-1 rounded-full bg-steel-blue" />
                    <span className="font-mono text-xs font-medium uppercase tracking-wider text-steel-blue">
                      Pro-Regulation
                    </span>
                  </div>
                  <p className="text-pretty text-[15px] leading-relaxed text-charcoal/80">
                    "Without international treaties, we risk a race to the bottom where
                    safety is sacrificed for competitive advantage. The Montreal
                    Protocol shows global cooperation on existential risk is both
                    possible and effective…"
                  </p>
                </div>
                <div className="p-5 sm:p-6">
                  <div className="mb-2.5 flex items-center gap-2">
                    <span className="h-3 w-1 rounded-full bg-vermilion" />
                    <span className="font-mono text-xs font-medium uppercase tracking-wider text-vermilion">
                      Pro-Innovation
                    </span>
                  </div>
                  <p className="text-pretty text-[15px] leading-relaxed text-charcoal/80">
                    "Treaties move at the pace of the slowest member state while AI
                    advances exponentially. The innovation that could cure diseases and
                    solve climate change would be stifled by bureaucratic oversight…"
                  </p>
                </div>
              </div>

              {/* Vote bar */}
              <div className="border-t border-black/[0.06] px-5 py-4 sm:px-6">
                <div className="mb-2 flex items-center justify-between text-xs">
                  <span className="font-mono font-medium text-steel-blue">47% For</span>
                  <span className="font-mono text-ink-muted">8,432 votes</span>
                  <span className="font-mono font-medium text-vermilion">53% Against</span>
                </div>
                <div className="vs-track">
                  <div className="bg-steel-blue" style={{ width: '47%' }} />
                  <span className="w-[3px] flex-shrink-0 bg-white" />
                  <div className="flex-1 bg-vermilion" />
                </div>
              </div>
            </motion.div>
          </section>

          {/* ── How it works ── */}
          <section className="border-t border-black/[0.07]">
            <div className="mx-auto max-w-6xl px-6 py-20">
              <motion.div {...inView} variants={fadeUp} className="mx-auto max-w-2xl text-center">
                <span className="eyebrow">How it works</span>
                <h2 className="mt-4 text-balance font-serif text-3xl tracking-tight text-charcoal sm:text-4xl">
                  From question to community verdict
                </h2>
              </motion.div>

              <motion.div
                {...inView}
                variants={stagger}
                className="mt-12 grid grid-cols-1 overflow-hidden rounded-2xl border border-black/[0.08] bg-white/80 backdrop-blur-sm md:grid-cols-3 md:divide-x md:divide-black/[0.08]"
              >
                {[
                  {
                    n: '01',
                    title: 'Create in the Arena',
                    description: 'Start a private debate on any balanced question — instantly.',
                  },
                  {
                    n: '02',
                    title: 'Watch AI debate',
                    description:
                      'Agents research, then argue both sides in real time: opening, rebuttal, closing.',
                  },
                  {
                    n: '03',
                    title: 'Share publicly',
                    description:
                      'When you’re ready, publish it so the community can read and vote.',
                  },
                ].map((step) => (
                  <motion.div
                    key={step.n}
                    variants={fadeUp}
                    className="group border-b border-black/[0.08] p-8 transition-colors duration-300 last:border-b-0 hover:bg-white md:border-b-0"
                  >
                    <span className="font-mono text-xs text-ink-muted/60">{step.n}</span>
                    <h3 className="mt-5 font-serif text-2xl text-charcoal">{step.title}</h3>
                    <p className="mt-3 text-pretty text-[15px] leading-relaxed text-ink-muted">
                      {step.description}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* ── Topic guidelines ── */}
          <section className="border-t border-black/[0.07]">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <motion.div {...inView} variants={fadeUp} className="mx-auto max-w-2xl text-center">
                <span className="eyebrow">Topic craft</span>
                <h2 className="mt-4 text-balance font-serif text-3xl tracking-tight text-charcoal sm:text-4xl">
                  What makes a great debate
                </h2>
              </motion.div>

              <motion.div
                {...inView}
                variants={stagger}
                className="mt-12 grid grid-cols-1 overflow-hidden rounded-2xl border border-black/[0.08] bg-white/85 backdrop-blur-sm sm:grid-cols-2 sm:divide-x sm:divide-black/[0.08]"
              >
                <motion.div variants={fadeUp} className="border-b border-black/[0.08] p-8 sm:border-b-0">
                  <span className="font-mono text-xs uppercase tracking-eyebrow text-ink-muted">
                    Great topics have
                  </span>
                  <ul className="mt-5 space-y-4">
                    {[
                      ['Neutral framing', "Questions that don't presuppose an answer."],
                      ['Substantive arguments', 'Both sides can make compelling cases.'],
                      ['Clear scope', 'Specific enough to debate meaningfully.'],
                      ['Current relevance', 'Topics that matter to people today.'],
                    ].map(([title, body]) => (
                      <li key={title} className="flex items-start gap-3">
                        <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-charcoal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <div>
                          <p className="text-sm font-semibold text-charcoal">{title}</p>
                          <p className="mt-0.5 text-pretty text-sm text-ink-muted">{body}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div variants={fadeUp} className="p-8">
                  <span className="font-mono text-xs uppercase tracking-eyebrow text-ink-muted">
                    Topics to avoid
                  </span>
                  <ul className="mt-5 space-y-4">
                    {[
                      ['Settled facts', '"Is the Earth round?" has no debate value.'],
                      ['Pure preferences', '"Is chocolate better than vanilla?" is subjective.'],
                      ['Loaded questions', 'Questions that assume controversial premises.'],
                      ['Inflammatory bait', 'Designed to provoke rather than enlighten.'],
                    ].map(([title, body]) => (
                      <li key={title} className="flex items-start gap-3">
                        <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <div>
                          <p className="text-sm font-semibold text-charcoal/60">{title}</p>
                          <p className="mt-0.5 text-pretty text-sm text-ink-muted/80">{body}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ── Final CTA ── */}
          <section className="px-6 pb-24 pt-4">
            <motion.div
              {...inView}
              variants={fadeUp}
              className="blueprint-grid-dark relative mx-auto max-w-5xl overflow-hidden rounded-[28px] bg-charcoal px-8 py-16 text-center sm:py-20"
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{
                  background:
                    'radial-gradient(60% 120% at 15% 0%, rgba(58,104,160,0.45), transparent 60%), radial-gradient(60% 120% at 85% 100%, rgba(229,68,45,0.40), transparent 60%)',
                }}
              />
              <div className="relative">
                <h2 className="text-balance font-serif text-3xl leading-tight tracking-tight text-white sm:text-5xl">
                  Start creating debates today.
                </h2>
                <p className="mx-auto mt-5 max-w-lg text-pretty text-lg leading-relaxed text-white/60">
                  Every debate you create contributes to more thoughtful discourse.
                </p>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <button
                    onClick={() => navigate('/signup')}
                    className="w-full rounded-full bg-white px-8 py-3.5 text-base font-medium text-charcoal transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:bg-white/90 sm:w-auto"
                  >
                    Sign up to start creating
                  </button>
                  <button
                    onClick={() => navigate('/explore')}
                    className="w-full rounded-full border border-white/25 px-8 py-3.5 text-base font-medium text-white transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:bg-white/10 sm:w-auto"
                  >
                    Watch public debates first
                  </button>
                </div>
                <p className="mt-8 font-mono text-[11px] uppercase tracking-eyebrow text-white/35">
                  Free to start · No spam · Cancel anytime
                </p>
              </div>
            </motion.div>
          </section>
        </div>
      </div>
    </MotionConfig>
  );
};

export default ArenaPreview;
